import React, { useState, useEffect } from "react";
import {
  Modal,
  Tabs,
  Group,
  Text,
  Button,
  ActionIcon,
  Box,
  Loader,
  Center,
  Stack,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notify";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import {
  ImageInfo,
  ImageUploadResponse,
} from "../../../types/staticWebGenerator";
import { BusinessInfo } from "../../../types/context";
import WebsiteGeneratorGallery from "./WebsiteGeneratorGallery";
import ImageUpload from "./ImageUpload";
import { marketingLibraryImages } from "../utils/photoUtils";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";

interface ImageData {
  id?: string | number;
  url?: string;
  src?: string;
  filename?: string;
  [key: string]: any;
}

interface WebsiteGeneratorPhotoGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  images?: ImageData[];
  selectedImages?: ImageData[];
  onImageSelect: (images: ImageData[]) => void;
  onImageDelete?: (image: ImageData) => void;
  onImageEdit?: (image: ImageData) => void;
  onConfirmSelection: () => void;
}

const WebsiteGeneratorPhotoGallery: React.FC<
  WebsiteGeneratorPhotoGalleryProps
> = ({
  isOpen,
  onClose,
  images: propImages = [],
  selectedImages = [],
  onImageSelect,
  onImageDelete,
  onImageEdit,
  onConfirmSelection,
}) => {
  const { getImages, deleteImage } = useApp();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [fetchedImages, setFetchedImages] = useState<ImageInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [localSelectedImages, setLocalSelectedImages] =
    useState<ImageData[]>(selectedImages);

  // Fetch images when modal opens
  useEffect(() => {
    const fetchImages = async () => {
      if (!isOpen || !businessInfo?.id) return;

      try {
        setIsLoading(true);
        const response = await getImages({
          salonId: businessInfo.id,
        });
        setFetchedImages(marketingLibraryImages(response.images));
      } catch (error) {
        console.error("Error fetching images:", error);
        notifyError("Failed to load images. Please try again.", {
          title: "Error",
          autoClose: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, [isOpen, businessInfo?.id, getImages]);

  useEffect(() => {
    setLocalSelectedImages(selectedImages);
  }, [selectedImages]);

  // Map ImageInfo[] to ImageData[] format
  const mapImageInfoToImageData = (imageInfo: ImageInfo): ImageData => ({
    id: imageInfo.filename,
    // Prefer CloudFront for reliable loading, then url, then s3_url fallback
    url: imageInfo.cloudfront_url || imageInfo.url || imageInfo.s3_url,
    src: imageInfo.cloudfront_url || imageInfo.url || imageInfo.s3_url,
    filename: imageInfo.filename,
  });

  // Use fetched images if available, otherwise use prop images
  const displayImages =
    fetchedImages.length > 0
      ? fetchedImages.map(mapImageInfoToImageData)
      : propImages;

  // Refresh images after upload
  const refreshImages = async () => {
    if (!businessInfo?.id) return;

    try {
      const response = await getImages({
        salonId: businessInfo.id,
      });
      setFetchedImages(marketingLibraryImages(response.images));
    } catch (error) {
      console.error("Error refreshing images:", error);
    }
  };

  // Handle image upload success
  const handleUploadSuccess = async (response: ImageUploadResponse) => {
    await refreshImages();
    notifySuccess("Image uploaded successfully", {
      title: "Success",
      autoClose: 3000,
    });
  };

  // Handle image delete
  // Note: Deletion confirmation is handled by WebsiteGeneratorGallery before calling this
  const handleDeleteImage = async (image: ImageData) => {
    if (!businessInfo?.id || !image.filename) return;

    try {
      await deleteImage({
        salonId: businessInfo.id,
        filename: image.filename,
        delete_thumbnail: true,
      });

      // Remove from fetched images
      setFetchedImages((prev) =>
        prev.filter((img) => img.filename !== image.filename),
      );

      // Also remove from local selected images if it was selected
      setLocalSelectedImages((prev) =>
        prev.filter((img) => img.id !== image.id),
      );

      // Call parent callback if provided
      if (onImageDelete) {
        onImageDelete(image);
      }

      notifySuccess("Image deleted successfully", {
        title: "Success",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting image:", error);
      notifyError("Failed to delete image. Please try again.", {
        title: "Error",
        autoClose: 5000,
      });
    }
  };

  const handleImageSelect = (image: ImageData) => {
    const isSelected = localSelectedImages.some((img) => img.id === image.id);
    if (isSelected) {
      setLocalSelectedImages((prev) =>
        prev.filter((img) => img.id !== image.id),
      );
    } else {
      // Check if we've reached the max limit (10 items)
      if (localSelectedImages.length >= 10) {
        notifyWarning("You can only select up to 10 images", {
          title: "Selection Limit",
          autoClose: 3000,
        });
        return;
      }
      setLocalSelectedImages((prev) => [...prev, image]);
    }
  };

  const handleConfirmSelection = () => {
    onImageSelect(localSelectedImages);
    onConfirmSelection();
    onClose();
  };

  const handleClose = () => {
    setLocalSelectedImages(selectedImages); // Reset to original selection
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      size="90%"
      title={
        <Group justify="space-between" style={{ width: "100%" }}>
          <Text fw={600} size="lg">
            Your Photos
          </Text>
          <ActionIcon variant="subtle" onClick={handleClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
      }
      styles={{
        content: { height: "90vh" },
        body: { height: "calc(90vh - 60px)", padding: 0 },
      }}
    >
      <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Tabs */}
        <Tabs
          defaultValue="gallery"
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Tabs.List style={{ padding: "16px 16px 0 16px" }}>
            <Tabs.Tab value="gallery">Gallery</Tabs.Tab>
          </Tabs.List>

          {/* Tab Content */}
          <Box style={{ flex: 1, padding: "16px", overflow: "auto" }}>
            <Tabs.Panel value="gallery">
              {isLoading ? (
                <Center p="xl">
                  <Loader size="md" />
                </Center>
              ) : (
                <Stack gap="lg">
                  {/* Upload Section */}
                  <ImageUpload
                    title=""
                    description=""
                    buttonText="Upload Photos"
                    onUpload={handleUploadSuccess}
                  />

                  {/* Gallery Section */}
                  <WebsiteGeneratorGallery
                    images={displayImages}
                    selectedImages={localSelectedImages}
                    onImageSelect={handleImageSelect}
                    onImageDelete={handleDeleteImage}
                    onImageEdit={onImageEdit}
                  />
                </Stack>
              )}
            </Tabs.Panel>
          </Box>
        </Tabs>

        {/* Bottom Action Bar */}
        <Box
          style={{
            padding: "16px",
            borderTop: `1px solid ${COLOR_BORDER_LIGHT}`,
            backgroundColor: COLOR_BACKGROUND_LIGHT,
          }}
        >
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {localSelectedImages.length} Selected
            </Text>
            <Group gap="sm">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSelection}>Add Selected</Button>
            </Group>
          </Group>
        </Box>
      </Box>
    </Modal>
  );
};

export default WebsiteGeneratorPhotoGallery;
